import { EntityType } from "@mapmycustomers/shared";

import { ApiMethodName } from "@app/store/api/ApiService";
import {
  EfficiencyTrackerCriteria,
  EfficiencyTrackerFieldType,
} from "@app/types/dashboard/cards/efficiencyTracker/EfficiencyTrackerCardConfiguration";
import EntityTypesSupportedInEfficiencyTracker from "@app/types/dashboard/cards/efficiencyTracker/EntityTypesSupportedInEfficiencyTracker";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";

export interface EfficiencyMetricDescriptor {
  dateField: string;
  entityType: EntityTypesSupportedInEfficiencyTracker;
  fetchMethod: ApiMethodName;
  field?: string;
  valueKind: "count" | "field";
}

const fetchMethodByEntityType: Record<EntityTypesSupportedInEfficiencyTracker, ApiMethodName> = {
  [EntityType.ACTIVITY]: "fetchActivities",
  [EntityType.COMPANY]: "fetchCompanies",
  [EntityType.DEAL]: "fetchDeals",
  [EntityType.PERSON]: "fetchPeople",
};

const PRESET_ENTITY: Partial<
  Record<EfficiencyTrackerFieldType, EntityTypesSupportedInEfficiencyTracker>
> = {
  facetime: EntityType.ACTIVITY,
  newCompanies: EntityType.COMPANY,
  wonRevenue: EntityType.DEAL,
};

export const getEfficiencyMetricDescriptor = (
  criteria: Pick<EfficiencyTrackerCriteria, "entityType" | "fieldName" | "fieldType">,
  fieldModel: Pick<FieldModel, "getByName">
): EfficiencyMetricDescriptor => {
  const entityType =
    PRESET_ENTITY[criteria.fieldType] ?? criteria.entityType ?? EntityType.ACTIVITY;
  const fetchMethod = fetchMethodByEntityType[entityType];

  switch (criteria.fieldType) {
    case "facetime":
      return {
        dateField: "completedAt",
        entityType,
        fetchMethod,
        field: "faceTime",
        valueKind: "field",
      };
    case "wonRevenue":
      return {
        dateField: "closingDate",
        entityType,
        fetchMethod,
        field: "amount",
        valueKind: "field",
      };
    case "custom": {
      const field = criteria.fieldName ? fieldModel.getByName(criteria.fieldName) : undefined;
      return {
        dateField: "createdAt",
        entityType,
        fetchMethod,
        field: field?.platformName,
        valueKind: "field",
      };
    }
    case "count":
    case "newCompanies":
    default:
      return { dateField: "createdAt", entityType, fetchMethod, valueKind: "count" };
  }
};

export default getEfficiencyMetricDescriptor;
